/* ==========================================================================
   Theme Features JS
   New UX/Navigation features: announcement bar, cart drawer, predictive
   search, mega menu, mobile menu drawer.
   Uses native fetch() for AJAX to avoid jQuery version conflicts.
   ========================================================================== */

(function() {
  'use strict';

  /* -----------------------------------------------------------------------
     Utilities
     ----------------------------------------------------------------------- */

  function debounce(fn, delay) {
    var timer;
    return function() {
      var context = this;
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function() {
        fn.apply(context, args);
      }, delay);
    };
  }

  /**
   * Escape HTML special characters to prevent XSS when inserting
   * strings from API responses into the DOM via innerHTML.
   */
  function escapeHtml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function formatMoney(cents, format) {
    if (cents == null || cents === undefined) cents = 0;
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }
    cents = parseInt(cents, 10) || 0;
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || theme.moneyFormat;

    function defaultOption(opt, def) {
      return (typeof opt === 'undefined' ? def : opt);
    }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultOption(precision, 2);
      thousands = defaultOption(thousands, ',');
      decimal = defaultOption(decimal, '.');

      if (isNaN(number) || number == null) {
        return 0;
      }

      number = (number / 100.0).toFixed(precision);

      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var cents_part = parts[1] ? (decimal + parts[1]) : '';

      return dollars + cents_part;
    }

    var match = formatString.match(placeholderRegex);
    if (!match) return String(cents);
    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
    }

    return formatString.replace(placeholderRegex, value);
  }

  /* -----------------------------------------------------------------------
     1. ANNOUNCEMENT BAR
     ----------------------------------------------------------------------- */

  theme.AnnouncementBar = (function() {
    function AnnouncementBar(container) {
      this.container = container;
      if (!this.container) return;

      this.toggleBtn = this.container.querySelector('[data-action="toggle-newsletter"]');
      this.newsletter = this.container.querySelector('.announcement-bar__newsletter');
      this.closeBtn = this.container.querySelector('.announcement-bar__newsletter-close');

      if (this.toggleBtn && this.newsletter) {
        this.toggleBtn.addEventListener('click', this._toggle.bind(this));
      }
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', this._close.bind(this));
      }

      // Handle newsletter form submission
      var form = this.container.querySelector('.announcement-bar__newsletter-form');
      if (form) {
        form.addEventListener('submit', this._handleSubmit.bind(this));
      }
    }

    AnnouncementBar.prototype._toggle = function() {
      var isOpen = this.newsletter.classList.contains('is-open');
      if (isOpen) {
        this._close();
      } else {
        this.newsletter.classList.add('is-open');
        this.toggleBtn.setAttribute('aria-expanded', 'true');
      }
    };

    AnnouncementBar.prototype._close = function() {
      this.newsletter.classList.remove('is-open');
      if (this.toggleBtn) {
        this.toggleBtn.setAttribute('aria-expanded', 'false');
      }
    };

    AnnouncementBar.prototype._handleSubmit = function(e) {
      e.preventDefault();
      var form = e.target;
      var emailInput = form.querySelector('input[type="email"]');
      var email = emailInput ? emailInput.value : '';
      if (!email) return;

      var formData = new FormData(form);

      fetch('/contact', {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      }).then(function() {
        if (emailInput) {
          emailInput.value = '';
          emailInput.placeholder = 'Thank you!';
        }
        setTimeout(function() {
          this._close();
        }.bind(this), 2000);
      }.bind(this));
    };

    return AnnouncementBar;
  })();

  /* -----------------------------------------------------------------------
     2. CART DRAWER
     ----------------------------------------------------------------------- */

  theme.CartDrawer = (function() {
    var selectors = {
      drawer: '#CartDrawer',
      overlay: '.cart-drawer__overlay',
      close: '.cart-drawer__close',
      body: '.cart-drawer__body',
      footer: '.cart-drawer__footer',
      count: '.cart-drawer__count',
      subtotal: '.cart-drawer__subtotal-price',
      shippingBar: '.cart-drawer__shipping-bar-fill',
      shippingMessage: '.cart-drawer__shipping-message',
      loading: '.cart-drawer__loading'
    };

    function CartDrawer() {
      this.drawer = document.querySelector(selectors.drawer);
      if (!this.drawer) return;

      this.overlay = document.querySelector(selectors.overlay);
      this.isOpen = false;

      this._bindEvents();
      this._interceptAddToCart();
    }

    CartDrawer.prototype._bindEvents = function() {
      // Close button
      var closeBtn = this.drawer.querySelector(selectors.close);
      if (closeBtn) {
        closeBtn.addEventListener('click', this.close.bind(this));
      }

      // Overlay click
      if (this.overlay) {
        this.overlay.addEventListener('click', this.close.bind(this));
      }

      // Escape key
      document.addEventListener('keyup', function(e) {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
        }
      }.bind(this));

      // Cart icon triggers (desktop + mobile)
      document.querySelectorAll('[data-cart-drawer-toggle]').forEach(function(el) {
        el.addEventListener('click', function(e) {
          e.preventDefault();
          this.toggle();
        }.bind(this));
      }.bind(this));

      // Quantity change + remove (delegated)
      this.drawer.addEventListener('click', function(e) {
        var target = e.target.closest('[data-cart-action]');
        if (!target) return;

        var action = target.getAttribute('data-cart-action');
        var itemKey = target.getAttribute('data-item-key');
        var qtyEl = this.drawer.querySelector('[data-qty-key="' + itemKey + '"]');
        var current = qtyEl ? parseInt(qtyEl.textContent.trim(), 10) || 0 : 0;

        if (action === 'increase') {
          this._changeItem(itemKey, current + 1);
        } else if (action === 'decrease') {
          this._changeItem(itemKey, Math.max(0, current - 1));
        } else if (action === 'remove') {
          this._changeItem(itemKey, 0);
        }
      }.bind(this));
    };

    CartDrawer.prototype._interceptAddToCart = function() {
      document.addEventListener('submit', function(e) {
        var form = e.target;
        if (!form || !form.action) return;

        // Only intercept /cart/add forms
        if (form.action.indexOf('/cart/add') === -1) return;

        // Skip if Shopify dynamic checkout button
        if (e.submitter && e.submitter.classList.contains('shopify-payment-button__button')) return;

        e.preventDefault();

        var formData = new FormData(form);
        var requestedQty = parseInt(formData.get('quantity') || '1', 10);
        var variantId = formData.get('id');
        var cartDrawer = this;

        function showProductError(message) {
          var submitBtn = form.querySelector('[type="submit"], .product-form__cart-submit');
          if (!submitBtn) return;
          var existing = form.querySelector('.product-form__error');
          if (existing) existing.remove();
          var errorMsg = document.createElement('div');
          errorMsg.className = 'product-form__error';
          errorMsg.textContent = message;
          submitBtn.parentNode.insertBefore(errorMsg, submitBtn.nextSibling);
          setTimeout(function() { errorMsg.remove(); }, 5000);
        }

        // Check inventory client-side before adding
        var inventory = window.__variantInventory;
        if (inventory && inventory[variantId]) {
          var variantInfo = inventory[variantId];
          // Only check if inventory is tracked and overselling is not allowed
          if (variantInfo.inventory_management && variantInfo.inventory_policy === 'deny') {
            var maxAvailable = variantInfo.inventory_quantity;

            // Check how many are already in the cart
            cartDrawer._setLoading(true);
            fetch('/cart.js').then(function(r) { return r.json(); })
            .then(function(cart) {
              var inCart = 0;
              if (cart && cart.items) {
                for (var i = 0; i < cart.items.length; i++) {
                  if (String(cart.items[i].variant_id) === String(variantId)) {
                    inCart = cart.items[i].quantity;
                    break;
                  }
                }
              }

              if (inCart + requestedQty > maxAvailable) {
                cartDrawer._setLoading(false);
                var remaining = maxAvailable - inCart;
                if (remaining <= 0) {
                  showProductError('You already have the maximum available quantity (' + maxAvailable + ') in your cart.');
                } else {
                  showProductError('Only ' + remaining + ' more can be added. You already have ' + inCart + ' in your cart.');
                }
                return;
              }

              // Inventory OK — proceed with add
              cartDrawer._doAddToCart(form, formData, showProductError);
            })
            .catch(function() {
              // If cart check fails, just try adding anyway
              cartDrawer._doAddToCart(form, formData, showProductError);
            });
            return;
          }
        }

        // No inventory tracking or data not available — add directly
        this._setLoading(true);
        this._doAddToCart(form, formData, showProductError);
      }.bind(this), true); // capture phase
    };

    CartDrawer.prototype._doAddToCart = function(form, formData, showProductError) {
      var cartDrawer = this;
      var submitBtn = form.querySelector('[type="submit"], .product-form__cart-submit');
      var btnTextEl = submitBtn ? submitBtn.querySelector('span') : null;
      var originalText = btnTextEl ? btnTextEl.textContent : '';

      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.classList.add('btn--loading');
        if (btnTextEl) btnTextEl.textContent = '';
      }

      function restoreButton() {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.classList.remove('btn--loading');
          if (btnTextEl) btnTextEl.textContent = originalText;
        }
      }

      fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      })
      .then(function(response) {
        if (!response.ok) {
          return response.json().then(function(data) {
            throw new Error(data.description || 'Could not add to cart');
          });
        }
        return response.json();
      })
      .then(function() {
        return cartDrawer._refreshCart();
      })
      .then(function() {
        cartDrawer._setLoading(false);
        restoreButton();
        cartDrawer.open();
      })
      .catch(function(err) {
        cartDrawer._setLoading(false);
        restoreButton();
        showProductError(err.message || 'Could not add to cart.');
      });
    };

    CartDrawer.prototype.open = function() {
      this.isOpen = true;
      document.documentElement.classList.add('js-drawer-open-right');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.drawer.focus();
    };

    CartDrawer.prototype.close = function() {
      this.isOpen = false;
      document.documentElement.classList.remove('js-drawer-open-right');
      this.drawer.setAttribute('aria-hidden', 'true');
    };

    CartDrawer.prototype.toggle = function() {
      if (this.isOpen) {
        this.close();
      } else {
        this._refreshCart().then(function() {
          this.open();
        }.bind(this));
      }
    };

    CartDrawer.prototype._changeItem = function(itemKey, quantity) {
      // Prevent rapid-fire requests
      if (this._changePending) return;
      this._changePending = true;
      this._setLoading(true);

      var requestedQty = quantity;

      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: itemKey, quantity: quantity })
      })
      .then(function(r) {
        return r.json().then(function(data) {
          return { ok: r.ok, data: data };
        });
      })
      .then(function(result) {
        if (result.data && result.data.items) {
          this._renderCart(result.data);
          this._updateCartCounts(result.data.item_count);

          // Check if Shopify capped the quantity (inventory limit)
          if (requestedQty > 0) {
            var actualItem = null;
            for (var i = 0; i < result.data.items.length; i++) {
              if (result.data.items[i].key === itemKey) {
                actualItem = result.data.items[i];
                break;
              }
            }
            if (actualItem && actualItem.quantity < requestedQty) {
              this._showItemError(itemKey, 'Only ' + actualItem.quantity + ' available for this item');
            }
          }
        } else if (!result.ok) {
          var msg = (result.data && result.data.description) || 'Could not update quantity';
          this._showItemError(itemKey, msg);
        }
        this._setLoading(false);
        this._changePending = false;
      }.bind(this))
      .catch(function(err) {
        console.error('Cart change error:', err);
        this._setLoading(false);
        this._changePending = false;
      }.bind(this));
    };

    CartDrawer.prototype._showItemError = function(itemKey, message) {
      var qtyEl = this.drawer.querySelector('[data-qty-key="' + itemKey + '"]');
      if (!qtyEl) return;
      var itemRow = qtyEl.closest('.cart-drawer__item');
      if (!itemRow) return;

      // Remove any existing error
      var existing = itemRow.parentNode.querySelector('.cart-drawer__item-error[data-error-key="' + itemKey + '"]');
      if (existing) existing.remove();

      var errorMsg = document.createElement('div');
      errorMsg.className = 'cart-drawer__item-error';
      errorMsg.setAttribute('data-error-key', itemKey);
      errorMsg.textContent = message;

      // Insert after the item row, not inside it
      itemRow.parentNode.insertBefore(errorMsg, itemRow.nextSibling);

      setTimeout(function() { errorMsg.remove(); }, 4000);
    };

    CartDrawer.prototype._refreshCart = function() {
      return fetch('/cart.js')
        .then(function(r) { return r.json(); })
        .then(function(cart) {
          this._renderCart(cart);
          this._updateCartCounts(cart.item_count);
        }.bind(this));
    };

    CartDrawer.prototype._renderCart = function(cart) {
      var body = this.drawer.querySelector(selectors.body);
      var footer = this.drawer.querySelector(selectors.footer);

      if (!body) return;

      // Clear existing content safely
      while (body.firstChild) {
        body.removeChild(body.firstChild);
      }

      if (cart.item_count === 0) {
        var emptyDiv = document.createElement('div');
        emptyDiv.className = 'cart-drawer__empty';
        var emptyP = document.createElement('p');
        emptyP.textContent = 'Your cart is empty';
        var shopLink = document.createElement('a');
        shopLink.href = '/collections/all';
        shopLink.textContent = 'Continue Shopping';
        emptyDiv.appendChild(emptyP);
        emptyDiv.appendChild(shopLink);
        body.appendChild(emptyDiv);
        if (footer) footer.style.display = 'none';
        return;
      }

      for (var i = 0; i < cart.items.length; i++) {
        var item = cart.items[i];
        var line = i + 1;
        var imageSrc = '';
        if (item.image) {
          // Shopify cart API returns full URLs. Insert size before extension.
          // Handle both query-string URLs and clean URLs.
          var imgUrl = item.image;
          // Remove any existing size suffix (e.g., _300x300)
          imgUrl = imgUrl.replace(/_\d+x\d+\./, '.');
          imgUrl = imgUrl.replace(/_\d+x\./, '.');
          // Insert _160x before the file extension
          imageSrc = imgUrl.replace(/\.(\w{3,4})(\?.*)?$/, '_160x.$1$2');
        }

        var itemEl = document.createElement('div');
        itemEl.className = 'cart-drawer__item';

        // Image link
        var imgLink = document.createElement('a');
        imgLink.href = item.url;
        imgLink.className = 'cart-drawer__item-image';
        if (imageSrc) {
          var img = document.createElement('img');
          img.src = imageSrc;
          img.alt = escapeHtml(item.title);
          imgLink.appendChild(img);
        }
        itemEl.appendChild(imgLink);

        // Details container
        var details = document.createElement('div');
        details.className = 'cart-drawer__item-details';

        // Title
        var titleLink = document.createElement('a');
        titleLink.href = item.url;
        titleLink.className = 'cart-drawer__item-title';
        titleLink.textContent = item.product_title;
        details.appendChild(titleLink);

        // Variant
        if (item.variant_title) {
          var variantSpan = document.createElement('span');
          variantSpan.className = 'cart-drawer__item-variant';
          variantSpan.textContent = item.variant_title;
          details.appendChild(variantSpan);
        }

        // Bottom row (quantity + price)
        var bottomRow = document.createElement('div');
        bottomRow.className = 'cart-drawer__item-bottom';

        // Quantity controls
        var qtyWrap = document.createElement('div');
        qtyWrap.className = 'cart-drawer__quantity';

        var decBtn = document.createElement('button');
        decBtn.type = 'button';
        decBtn.setAttribute('data-cart-action', 'decrease');
        decBtn.setAttribute('data-item-key', item.key);
        decBtn.setAttribute('aria-label', 'Decrease quantity');
        decBtn.textContent = '\u2212';

        var qtyVal = document.createElement('span');
        qtyVal.className = 'cart-drawer__quantity-value';
        qtyVal.setAttribute('data-qty-key', item.key);
        qtyVal.textContent = item.quantity;

        var incBtn = document.createElement('button');
        incBtn.type = 'button';
        incBtn.setAttribute('data-cart-action', 'increase');
        incBtn.setAttribute('data-item-key', item.key);
        incBtn.setAttribute('aria-label', 'Increase quantity');
        incBtn.textContent = '+';

        qtyWrap.appendChild(decBtn);
        qtyWrap.appendChild(qtyVal);
        qtyWrap.appendChild(incBtn);
        bottomRow.appendChild(qtyWrap);

        // Price
        var priceSpan = document.createElement('span');
        priceSpan.className = 'cart-drawer__item-price';
        priceSpan.textContent = formatMoney(item.final_line_price || item.line_price || (item.price * item.quantity));
        bottomRow.appendChild(priceSpan);

        details.appendChild(bottomRow);

        // Remove button
        var removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.className = 'cart-drawer__item-remove';
        removeBtn.setAttribute('data-cart-action', 'remove');
        removeBtn.setAttribute('data-item-key', item.key);
        removeBtn.textContent = 'Remove';
        details.appendChild(removeBtn);

        itemEl.appendChild(details);
        body.appendChild(itemEl);
      }

      if (footer) {
        footer.style.display = '';

        // Update subtotal
        var subtotalEl = footer.querySelector(selectors.subtotal);
        if (subtotalEl) {
          subtotalEl.textContent = formatMoney(cart.total_price);
        }

        // Update shipping threshold bar
        var shippingThreshold = parseInt(this.drawer.getAttribute('data-shipping-threshold') || '0', 10);
        if (shippingThreshold > 0) {
          var remaining = shippingThreshold * 100 - cart.total_price;
          var barFill = footer.querySelector(selectors.shippingBar);
          var msgEl = footer.querySelector(selectors.shippingMessage);

          if (barFill) {
            var pct = Math.min(100, (cart.total_price / (shippingThreshold * 100)) * 100);
            barFill.style.width = pct + '%';
          }

          if (msgEl) {
            // Build message safely using DOM
            while (msgEl.firstChild) msgEl.removeChild(msgEl.firstChild);
            if (remaining <= 0) {
              msgEl.appendChild(document.createTextNode('You qualify for '));
              var strong = document.createElement('strong');
              strong.textContent = 'free shipping!';
              msgEl.appendChild(strong);
            } else {
              msgEl.appendChild(document.createTextNode('Spend '));
              var strongAmt = document.createElement('strong');
              strongAmt.textContent = formatMoney(remaining);
              msgEl.appendChild(strongAmt);
              msgEl.appendChild(document.createTextNode(' more for free shipping'));
            }
          }
        }
      }
    };

    CartDrawer.prototype._updateCartCounts = function(count) {
      // Update all cart count elements on the page
      document.querySelectorAll('#CartCount span:first-child, .cart-drawer__count').forEach(function(el) {
        el.textContent = count;
        if (el.classList.contains('mobile-nav-cart-count')) {
          el.style.display = count > 0 ? '' : 'none';
        }
      });
    };

    CartDrawer.prototype._setLoading = function(loading) {
      var el = this.drawer.querySelector(selectors.loading);
      if (el) {
        el.classList.toggle('is-active', loading);
      }
    };

    return CartDrawer;
  })();

  /* -----------------------------------------------------------------------
     3. PREDICTIVE SEARCH
     ----------------------------------------------------------------------- */

  theme.PredictiveSearch = (function() {
    function PredictiveSearch() {
      var allInputs = document.querySelectorAll('.search-bar__input, .search__input, .search-header__input');
      this.inputs = [];
      for (var i = 0; i < allInputs.length; i++) {
        if (allInputs[i].id !== 'SearchInput') {
          this.inputs.push(allInputs[i]);
        }
      }
      if (!this.inputs.length) return;

      this.activeInput = null;
      this.activeIndex = -1;
      this.results = [];

      this._createContainers();
      this._bindEvents();
    }

    PredictiveSearch.prototype._createContainers = function() {
      this.inputs.forEach(function(input) {
        // Find parent form wrapper for positioning
        var wrapper = input.closest('.search-bar__form-wrapper') ||
                      input.closest('.search-bar__table') ||
                      input.closest('.search') ||
                      input.parentElement;

        // Skip if already has a predictive search container
        if (wrapper.querySelector('.predictive-search')) return;

        var container = document.createElement('div');
        container.className = 'predictive-search';
        container.setAttribute('role', 'listbox');
        container.setAttribute('aria-label', 'Search suggestions');
        wrapper.appendChild(container);

        // Store reference
        input._predictiveContainer = container;
        input.setAttribute('autocomplete', 'off');
        input.setAttribute('aria-autocomplete', 'list');
      });
    };

    PredictiveSearch.prototype._bindEvents = function() {
      var debouncedSearch = debounce(this._doSearch.bind(this), 250);

      this.inputs.forEach(function(input) {
        input.addEventListener('input', function(e) {
          this.activeInput = e.target;
          debouncedSearch(e.target.value.trim());
        }.bind(this));

        input.addEventListener('focus', function(e) {
          this.activeInput = e.target;
          var query = e.target.value.trim();
          if (query.length >= 2 && e.target._predictiveContainer) {
            e.target._predictiveContainer.classList.add('is-open');
          }
        }.bind(this));

        input.addEventListener('keydown', function(e) {
          this._handleKeydown(e);
        }.bind(this));
      }.bind(this));

      // Close on click outside
      document.addEventListener('click', function(e) {
        if (!e.target.closest('.predictive-search') && !e.target.closest('.search-bar__form-wrapper') && !e.target.closest('.search')) {
          this._closeAll();
        }
      }.bind(this));
    };

    PredictiveSearch.prototype._doSearch = function(query) {
      if (!this.activeInput) return;

      var container = this.activeInput._predictiveContainer;
      if (!container) return;

      if (query.length < 2) {
        container.classList.remove('is-open');
        while (container.firstChild) container.removeChild(container.firstChild);
        this.activeIndex = -1;
        return;
      }

      // Show loading state
      while (container.firstChild) container.removeChild(container.firstChild);
      var loadingEl = document.createElement('div');
      loadingEl.className = 'predictive-search__loading';
      loadingEl.textContent = 'Searching...';
      container.appendChild(loadingEl);
      container.classList.add('is-open');

      var url = '/search/suggest.json?q=' + encodeURIComponent(query) +
                '&resources[type]=product' +
                '&resources[limit]=8';

      fetch(url)
        .then(function(r) { return r.json(); })
        .then(function(data) {
          this.activeIndex = -1;
          this._renderResults(container, data.resources.results, query);
        }.bind(this))
        .catch(function() {
          while (container.firstChild) container.removeChild(container.firstChild);
          container.classList.remove('is-open');
        });
    };

    PredictiveSearch.prototype._renderResults = function(container, results, query) {
      while (container.firstChild) container.removeChild(container.firstChild);
      var hasResults = false;
      this.results = [];

      // Helper to create a result category
      function createCategory(title) {
        var cat = document.createElement('div');
        cat.className = 'predictive-search__category';
        var heading = document.createElement('p');
        heading.className = 'predictive-search__category-title';
        heading.textContent = title;
        cat.appendChild(heading);
        return cat;
      }

      // Products
      if (results.products && results.products.length > 0) {
        hasResults = true;
        var cat = createCategory('Products');
        for (var i = 0; i < results.products.length; i++) {
          var p = results.products[i];
          var imgSrc = p.image ? p.image.replace(/(\.[^.]+)$/, '_100x$1') : '';
          this.results.push(p.url);

          var link = document.createElement('a');
          link.href = p.url;
          link.className = 'predictive-search__item';
          link.setAttribute('data-index', this.results.length - 1);

          if (imgSrc) {
            var imgWrap = document.createElement('div');
            imgWrap.className = 'predictive-search__item-image';
            var img = document.createElement('img');
            img.src = imgSrc;
            img.alt = '';
            imgWrap.appendChild(img);
            link.appendChild(imgWrap);
          }

          var info = document.createElement('div');
          info.className = 'predictive-search__item-info';
          var title = document.createElement('p');
          title.className = 'predictive-search__item-title';
          title.textContent = p.title;
          info.appendChild(title);
          if (p.product_type) {
            var meta = document.createElement('span');
            meta.className = 'predictive-search__item-meta';
            meta.textContent = p.product_type;
            info.appendChild(meta);
          }
          link.appendChild(info);

          if (p.price_min) {
            var priceSpan = document.createElement('span');
            priceSpan.className = 'predictive-search__item-price';
            priceSpan.textContent = formatMoney(parseFloat(p.price_min) * 100);
            link.appendChild(priceSpan);
          }

          cat.appendChild(link);
        }
        container.appendChild(cat);
      }

      // Collections
      if (results.collections && results.collections.length > 0) {
        hasResults = true;
        var colCat = createCategory('Collections');
        for (var c = 0; c < results.collections.length; c++) {
          var col = results.collections[c];
          this.results.push(col.url);
          var colLink = document.createElement('a');
          colLink.href = col.url;
          colLink.className = 'predictive-search__item';
          colLink.setAttribute('data-index', this.results.length - 1);
          var colInfo = document.createElement('div');
          colInfo.className = 'predictive-search__item-info';
          var colTitle = document.createElement('p');
          colTitle.className = 'predictive-search__item-title';
          colTitle.textContent = col.title;
          colInfo.appendChild(colTitle);
          colLink.appendChild(colInfo);
          colCat.appendChild(colLink);
        }
        container.appendChild(colCat);
      }

      // Articles
      if (results.articles && results.articles.length > 0) {
        hasResults = true;
        var artCat = createCategory('Articles');
        for (var a = 0; a < results.articles.length; a++) {
          var art = results.articles[a];
          this.results.push(art.url);
          var artLink = document.createElement('a');
          artLink.href = art.url;
          artLink.className = 'predictive-search__item';
          artLink.setAttribute('data-index', this.results.length - 1);
          var artInfo = document.createElement('div');
          artInfo.className = 'predictive-search__item-info';
          var artTitle = document.createElement('p');
          artTitle.className = 'predictive-search__item-title';
          artTitle.textContent = art.title;
          artInfo.appendChild(artTitle);
          if (art.author) {
            var artMeta = document.createElement('span');
            artMeta.className = 'predictive-search__item-meta';
            artMeta.textContent = 'by ' + art.author;
            artInfo.appendChild(artMeta);
          }
          artLink.appendChild(artInfo);
          artCat.appendChild(artLink);
        }
        container.appendChild(artCat);
      }

      // Pages
      if (results.pages && results.pages.length > 0) {
        hasResults = true;
        var pgCat = createCategory('Pages');
        for (var pg = 0; pg < results.pages.length; pg++) {
          var page = results.pages[pg];
          this.results.push(page.url);
          var pgLink = document.createElement('a');
          pgLink.href = page.url;
          pgLink.className = 'predictive-search__item';
          pgLink.setAttribute('data-index', this.results.length - 1);
          var pgInfo = document.createElement('div');
          pgInfo.className = 'predictive-search__item-info';
          var pgTitle = document.createElement('p');
          pgTitle.className = 'predictive-search__item-title';
          pgTitle.textContent = page.title;
          pgInfo.appendChild(pgTitle);
          pgLink.appendChild(pgInfo);
          pgCat.appendChild(pgLink);
        }
        container.appendChild(pgCat);
      }

      if (!hasResults) {
        var emptyDiv = document.createElement('div');
        emptyDiv.className = 'predictive-search__empty';
        emptyDiv.textContent = 'No results found';
        container.appendChild(emptyDiv);
      } else {
        var viewAll = document.createElement('a');
        viewAll.href = '/search?q=' + encodeURIComponent(query);
        viewAll.className = 'predictive-search__view-all';
        viewAll.textContent = 'View all results';
        container.appendChild(viewAll);
      }
    };

    PredictiveSearch.prototype._handleKeydown = function(e) {
      var container = this.activeInput ? this.activeInput._predictiveContainer : null;
      if (!container || !container.classList.contains('is-open')) return;

      var items = container.querySelectorAll('.predictive-search__item');

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.activeIndex = Math.min(this.activeIndex + 1, items.length - 1);
        this._highlightItem(items);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.activeIndex = Math.max(this.activeIndex - 1, -1);
        this._highlightItem(items);
      } else if (e.key === 'Enter' && this.activeIndex >= 0 && items[this.activeIndex]) {
        e.preventDefault();
        items[this.activeIndex].click();
      } else if (e.key === 'Escape') {
        this._closeAll();
        this.activeInput.blur();
      }
    };

    PredictiveSearch.prototype._highlightItem = function(items) {
      items.forEach(function(item) { item.classList.remove('is-active'); });
      if (this.activeIndex >= 0 && items[this.activeIndex]) {
        items[this.activeIndex].classList.add('is-active');
        items[this.activeIndex].scrollIntoView({ block: 'nearest' });
      }
    };

    PredictiveSearch.prototype._closeAll = function() {
      document.querySelectorAll('.predictive-search').forEach(function(el) {
        el.classList.remove('is-open');
      });
      this.activeIndex = -1;
    };

    return PredictiveSearch;
  })();

  /* -----------------------------------------------------------------------
     4. MEGA MENU
     ----------------------------------------------------------------------- */

  theme.MegaMenu = (function() {
    function MegaMenu() {
      this.megaItems = document.querySelectorAll('[data-mega-menu]');
      if (!this.megaItems.length) return;

      this._bindEvents();
    }

    MegaMenu.prototype._bindEvents = function() {
      this.megaItems.forEach(function(item) {
        var panel = item.querySelector('.site-nav__mega-menu');
        var button = item.querySelector('.site-nav__link--button');
        if (!panel) return;

        var openTimer = null;
        var closeTimer = null;

        // Hover intent
        item.addEventListener('mouseenter', function() {
          clearTimeout(closeTimer);
          openTimer = setTimeout(function() {
            this._open(panel, button);
          }.bind(this), 150);
        }.bind(this));

        item.addEventListener('mouseleave', function() {
          clearTimeout(openTimer);
          closeTimer = setTimeout(function() {
            this._close(panel, button);
          }.bind(this), 200);
        }.bind(this));

        // Click toggle (accessibility)
        if (button) {
          button.addEventListener('click', function(e) {
            e.preventDefault();
            if (panel.classList.contains('is-open')) {
              this._close(panel, button);
            } else {
              this._closeAll();
              this._open(panel, button);
            }
          }.bind(this));
        }

        // Close on Escape
        item.addEventListener('keydown', function(e) {
          if (e.key === 'Escape') {
            this._close(panel, button);
            if (button) button.focus();
          }
        }.bind(this));
      }.bind(this));

      // Close all on click outside
      document.addEventListener('click', function(e) {
        if (!e.target.closest('[data-mega-menu]')) {
          this._closeAll();
        }
      }.bind(this));
    };

    MegaMenu.prototype._open = function(panel, button) {
      panel.classList.add('is-open');
      if (button) button.setAttribute('aria-expanded', 'true');
    };

    MegaMenu.prototype._close = function(panel, button) {
      panel.classList.remove('is-open');
      if (button) button.setAttribute('aria-expanded', 'false');
    };

    MegaMenu.prototype._closeAll = function() {
      document.querySelectorAll('.site-nav__mega-menu.is-open').forEach(function(el) {
        el.classList.remove('is-open');
      });
      document.querySelectorAll('[data-mega-menu] .site-nav__link--button').forEach(function(btn) {
        btn.setAttribute('aria-expanded', 'false');
      });
    };

    return MegaMenu;
  })();

  /* -----------------------------------------------------------------------
     5. MOBILE MENU DRAWER
     ----------------------------------------------------------------------- */

  theme.MobileMenuDrawer = (function() {
    function MobileMenuDrawer() {
      this.drawer = document.querySelector('#MobileMenuDrawer');
      if (!this.drawer) return;

      this.overlay = document.querySelector('.mobile-menu__overlay');
      this.isOpen = false;
      this.panelStack = [];

      // Override old MobileNav so it doesn't initialize
      if (theme.MobileNav && theme.MobileNav.init) {
        theme.MobileNav.init = function() {};
      }

      this._bindEvents();
    }

    MobileMenuDrawer.prototype._bindEvents = function() {
      // Toggle button (the hamburger)
      document.querySelectorAll('.js-mobile-nav-toggle').forEach(function(btn) {
        btn.addEventListener('click', function(e) {
          e.preventDefault();
          this.toggle();
        }.bind(this));
      }.bind(this));

      // Close button
      var closeBtn = this.drawer.querySelector('.mobile-menu__close');
      if (closeBtn) {
        closeBtn.addEventListener('click', this.close.bind(this));
      }

      // Overlay
      if (this.overlay) {
        this.overlay.addEventListener('click', this.close.bind(this));
      }

      // Mobile cart toggle — close mobile menu then open cart drawer
      document.querySelectorAll('[data-mobile-cart-toggle]').forEach(function(el) {
        el.addEventListener('click', function(e) {
          e.preventDefault();
          this.close();
          // Small delay to let close animation finish before opening cart
          setTimeout(function() {
            if (theme.cartDrawerInstance) {
              theme.cartDrawerInstance.toggle();
            }
          }, 350);
        }.bind(this));
      }.bind(this));

      // Escape key
      document.addEventListener('keyup', function(e) {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
        }
      }.bind(this));

      // Open sub-panel
      this.drawer.addEventListener('click', function(e) {
        var openBtn = e.target.closest('[data-open-panel]');
        if (openBtn) {
          e.preventDefault();
          var panelId = openBtn.getAttribute('data-open-panel');
          this._openPanel(panelId);
          return;
        }

        var backBtn = e.target.closest('[data-close-panel]');
        if (backBtn) {
          e.preventDefault();
          this._closeTopPanel();
          return;
        }

        // Collapsible toggle
        var toggle = e.target.closest('.mobile-menu__collapsible-toggle');
        if (toggle) {
          e.preventDefault();
          var targetId = toggle.getAttribute('aria-controls');
          var content = document.getElementById(targetId);
          if (content) {
            var isCurrentlyOpen = content.classList.contains('is-open');
            content.classList.toggle('is-open');
            toggle.classList.toggle('is-open');
            toggle.setAttribute('aria-expanded', String(!isCurrentlyOpen));
          }
        }
      }.bind(this));
    };

    MobileMenuDrawer.prototype.open = function() {
      this.isOpen = true;
      document.documentElement.classList.add('js-drawer-open-left');
      this.drawer.setAttribute('aria-hidden', 'false');

      // Hide old mobile nav if still in DOM
      var oldNav = document.querySelector('.mobile-nav-wrapper');
      if (oldNav) oldNav.style.display = 'none';
    };

    MobileMenuDrawer.prototype.close = function() {
      this.isOpen = false;
      document.documentElement.classList.remove('js-drawer-open-left');
      this.drawer.setAttribute('aria-hidden', 'true');

      // Reset all panels
      this.panelStack = [];
      this.drawer.querySelectorAll('.mobile-menu__panel--nested').forEach(function(p) {
        p.classList.remove('is-open');
      });
    };

    MobileMenuDrawer.prototype.toggle = function() {
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    };

    MobileMenuDrawer.prototype._openPanel = function(panelId) {
      var panel = document.getElementById(panelId);
      if (!panel) return;

      this.panelStack.push(panelId);
      panel.classList.add('is-open');
    };

    MobileMenuDrawer.prototype._closeTopPanel = function() {
      if (this.panelStack.length === 0) return;

      var panelId = this.panelStack.pop();
      var panel = document.getElementById(panelId);
      if (panel) {
        panel.classList.remove('is-open');
      }
    };

    return MobileMenuDrawer;
  })();

  /* -----------------------------------------------------------------------
     6. COLLECTION FILTERS (AJAX + mobile drawer + sort dropdown)
     ----------------------------------------------------------------------- */

  theme.CollectionFilters = (function() {
    function CollectionFilters(container) {
      this.container = container;
      if (!this.container) return;

      this.sectionId = this.container.getAttribute('data-section-id');
      this.productGrid = document.getElementById('CollectionProducts');

      this._bindFilterEvents();
      this._bindSortDropdown();
      this._bindMobileDrawer();
      this._bindFilterToggles();
      this._bindFilterSearch();
      this._syncTagLinksWithQueryParams();
      this._enforcePriceFilter();
    }

    /* --- Filter form changes (checkboxes) --- */
    CollectionFilters.prototype._bindFilterEvents = function() {
      // Delegated listener for all filter checkboxes
      document.addEventListener('change', function(e) {
        var input = e.target;
        if (!input.closest || !input.closest('#CollectionFiltersForm, .collection-filters-drawer__body #CollectionFiltersForm')) return;
        if (input.type !== 'checkbox') return;

        this._submitFilters(input.closest('form'));
      }.bind(this));

      // Price filter apply button
      document.addEventListener('click', function(e) {
        var btn = e.target.closest('[data-action="apply-price-filter"]');
        if (!btn) return;
        var form = btn.closest('form');
        if (form) this._submitFilters(form);
      }.bind(this));

      // Remove individual filter pill
      document.addEventListener('click', function(e) {
        var pill = e.target.closest('[data-action="remove-filter"]');
        if (!pill) return;
        e.preventDefault();
        this._navigateAndReload(pill.href);
      }.bind(this));

      // Clear all filters
      document.addEventListener('click', function(e) {
        var clear = e.target.closest('[data-action="clear-filters"]');
        if (!clear) return;
        e.preventDefault();
        this._navigateAndReload(clear.href);
      }.bind(this));

      // Tag-based filter links (add/remove tag) — intercept for AJAX
      // Also handle taps on the parent label/list-item, since on mobile the <label>
      // can intercept the touch without forwarding to the nested <a> tag link.
      document.addEventListener('click', function(e) {
        var tagLink = e.target.closest('.collection-filters__tag-link, [data-action="remove-tag-filter"]');
        if (!tagLink) {
          // Check if tap landed on a tag label/list-item that contains a tag link
          var listItem = e.target.closest('.collection-filters__tag-list .collection-filters__list-item');
          if (listItem) {
            tagLink = listItem.querySelector('.collection-filters__tag-link');
          }
        }
        if (!tagLink) return;
        e.preventDefault();
        this._navigateAndReload(tagLink.href);
      }.bind(this));
    };

    CollectionFilters.prototype._submitFilters = function(form) {
      if (!form) return;
      var formData = new FormData(form);
      var params = new URLSearchParams(formData).toString();
      var url = window.location.pathname + '?' + params;
      this._navigateAndReload(url);
    };

    CollectionFilters.prototype._navigateAndReload = function(url) {
      window.history.pushState({}, '', url);
      this._fetchAndReplace(url);
    };

    CollectionFilters.prototype._fetchAndReplace = function(url) {
      // Show loading state
      if (this.productGrid) {
        this.productGrid.style.opacity = '0.5';
        this.productGrid.style.pointerEvents = 'none';
      }

      // Two fetches in parallel:
      //  - section_id  -> sidebar, toolbar, mobile drawer, active-filters bar
      //  - view=ajax   -> grid + pagination (48 per page, in-stock first)
      //
      // The AJAX template (templates/collection.ajax.liquid) is the same
      // one used by the initial page swap and pagination clicks, so the
      // grid size and pagination counts stay consistent across page load,
      // filter changes, and next/prev pagination clicks. The section
      // response is still needed because sidebar/toolbar markup isn't in
      // the AJAX template. Both happen in one Promise.all callback so the
      // DOM is updated in a single JS task — no 18-then-48 flicker.
      var sectionUrl = new URL(url, window.location.origin);
      sectionUrl.searchParams.set('section_id', this.sectionId);

      var ajaxViewUrl = new URL(url, window.location.origin);
      ajaxViewUrl.searchParams.set('view', 'ajax');

      var sectionFetch = fetch(sectionUrl.toString(), { credentials: 'same-origin' })
        .then(function(r) { return r.text(); });
      var ajaxFetch = fetch(ajaxViewUrl.toString(), {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      }).then(function(r) { return r.text(); });

      Promise.all([sectionFetch, ajaxFetch])
        .then(function(results) {
          var parser = new DOMParser();
          var sectionDoc = parser.parseFromString(results[0], 'text/html');
          var ajaxDoc = parser.parseFromString(results[1], 'text/html');

          // Preserve scroll position before DOM replacement
          var scrollX = window.pageXOffset;
          var scrollY = window.pageYOffset;

          // Replace #CollectionProducts from the section response. This
          // pulls in the refreshed active-filters bar and search row. The
          // grid inside is briefly the 18-per-page server render — we
          // overwrite it with the 48-per-page AJAX grid below, all in the
          // same task so no flicker is visible.
          var newProducts = sectionDoc.getElementById('CollectionProducts');
          if (newProducts && this.productGrid) {
            this.productGrid.parentNode.replaceChild(newProducts, this.productGrid);
            this.productGrid = newProducts;
            this.productGrid.style.opacity = '1';
            this.productGrid.style.pointerEvents = '';
          }

          // Replace desktop sidebar filters
          var newSidebar = sectionDoc.querySelector('.collection-layout__sidebar');
          var oldSidebar = document.querySelector('.collection-layout__sidebar');
          if (newSidebar && oldSidebar) {
            oldSidebar.parentNode.replaceChild(newSidebar, oldSidebar);
          }

          // Replace mobile drawer filters — keep drawer open if it was open
          var mobileDrawerWasOpen = document.body.classList.contains('mobile-filters-open');
          var newDrawer = sectionDoc.getElementById('MobileFiltersDrawer');
          var oldDrawer = document.getElementById('MobileFiltersDrawer');
          if (newDrawer && oldDrawer) {
            // Preserve scroll position within the drawer body
            var oldBody = oldDrawer.querySelector('.collection-filters-drawer__body');
            var drawerScrollTop = oldBody ? oldBody.scrollTop : 0;

            oldDrawer.parentNode.replaceChild(newDrawer, oldDrawer);

            // Re-open the drawer if it was open before the AJAX replace
            if (mobileDrawerWasOpen) {
              newDrawer.setAttribute('aria-hidden', 'false');
              var newBody = newDrawer.querySelector('.collection-filters-drawer__body');
              if (newBody) newBody.scrollTop = drawerScrollTop;
            }
          }

          // Replace toolbar (filter count badge, sort, results count)
          var newToolbar = sectionDoc.querySelector('.collection-toolbar');
          var oldToolbar = document.querySelector('.collection-toolbar');
          if (newToolbar && oldToolbar) {
            oldToolbar.parentNode.replaceChild(newToolbar, oldToolbar);
            this._bindSortDropdown();
          }

          // Swap grid + pagination from the AJAX template (48 per page).
          var ajaxResults = ajaxDoc.getElementById('CollectionAjaxResults');
          if (ajaxResults) {
            var freshGrid = ajaxResults.querySelector('.grid--view-items') || ajaxResults.querySelector('.collection-empty');
            var gridView = document.getElementById('gridView');
            if (gridView && freshGrid) {
              while (gridView.firstChild) gridView.removeChild(gridView.firstChild);
              gridView.appendChild(document.adoptNode(freshGrid));
            }

            var freshPagination = ajaxResults.querySelector('.pagination-nav-wrap');
            var oldPagination = document.querySelector('#Collection > .pagination-nav-wrap');
            var collectionEl = document.getElementById('Collection');
            if (freshPagination && oldPagination) {
              oldPagination.parentNode.replaceChild(document.adoptNode(freshPagination), oldPagination);
            } else if (freshPagination && collectionEl) {
              var layout = collectionEl.querySelector('.collection-layout');
              if (layout && layout.nextSibling) {
                collectionEl.insertBefore(document.adoptNode(freshPagination), layout.nextSibling);
              } else {
                collectionEl.appendChild(document.adoptNode(freshPagination));
              }
            } else if (!freshPagination && oldPagination) {
              oldPagination.parentNode.removeChild(oldPagination);
            }
          }

          // Re-bind filter group toggles, search, and sync tag links
          this._bindFilterToggles();
          this._bindFilterSearch();
          this._syncTagLinksWithQueryParams();
          this._enforcePriceFilter();

          // Restore scroll position after DOM replacement
          window.scrollTo(scrollX, scrollY);
        }.bind(this))
        .catch(function() {
          // On error, do a full page navigation
          window.location.href = url;
        });
    };

    /* --- Hide products whose price falls entirely outside the active price filter --- */
    CollectionFilters.prototype._enforcePriceFilter = function() {
      var params = new URLSearchParams(window.location.search);
      var filterMin = null;
      var filterMax = null;

      params.forEach(function(value, key) {
        if (key.indexOf('price.gte') !== -1) filterMin = parseFloat(value);
        if (key.indexOf('price.lte') !== -1) filterMax = parseFloat(value);
      });

      if (filterMin === null && filterMax === null) return;

      var hiddenCount = this._hideOutOfRangeProducts(filterMin, filterMax);

      // Backfill from next pages if products were hidden
      if (hiddenCount > 0) {
        this._backfillProducts(filterMin, filterMax, hiddenCount);
      }
    };

    CollectionFilters.prototype._isOutOfRange = function(card, filterMin, filterMax) {
      var priceMin = parseFloat(card.getAttribute('data-product-price-min')) / 100;
      var priceMax = parseFloat(card.getAttribute('data-product-price-max')) / 100;
      if (isNaN(priceMin) || isNaN(priceMax)) return false;
      if (filterMax !== null && priceMin > filterMax) return true;
      if (filterMin !== null && priceMax < filterMin) return true;
      return false;
    };

    CollectionFilters.prototype._hideOutOfRangeProducts = function(filterMin, filterMax) {
      var hiddenCount = 0;
      var cards = document.querySelectorAll('.product-card[data-product-price-min]');
      var self = this;
      cards.forEach(function(card) {
        var hide = self._isOutOfRange(card, filterMin, filterMax);
        var gridItem = card.closest('.grid__item, .list-view-item');
        if (gridItem) {
          gridItem.style.display = hide ? 'none' : '';
          if (hide) hiddenCount++;
        }
      });
      return hiddenCount;
    };

    CollectionFilters.prototype._getNextPageUrl = function(container) {
      var root = container || document;
      // New pagination: arrow link with icon-arrow-right
      var nextIcon = root.querySelector('.pagination-nav__arrow[aria-label="Next page"] .icon-arrow-right');
      if (nextIcon) {
        var link = nextIcon.closest('a');
        if (link) return link.href;
      }
      // Legacy pagination fallback
      var legacyIcon = root.querySelector('.pagination a .icon-arrow-right');
      if (legacyIcon) {
        var legacyLink = legacyIcon.closest('a');
        if (legacyLink) return legacyLink.href;
      }
      return null;
    };

    CollectionFilters.prototype._backfillProducts = function(filterMin, filterMax, needed) {
      var nextUrl = this._getNextPageUrl();
      if (!nextUrl) return;
      this._fetchBackfillPage(nextUrl, filterMin, filterMax, needed);
    };

    CollectionFilters.prototype._fetchBackfillPage = function(url, filterMin, filterMax, needed) {
      var self = this;
      var separator = url.indexOf('?') === -1 ? '?' : '&';
      var fetchUrl = url + separator + 'section_id=' + this.sectionId;

      fetch(fetchUrl)
        .then(function(r) { return r.text(); })
        .then(function(html) {
          var parser = new DOMParser();
          var doc = parser.parseFromString(html, 'text/html');

          // Get grid items from next page
          var gridContainer = document.querySelector('#CollectionProducts .grid--view-items');
          if (!gridContainer) return;

          var newItems = doc.querySelectorAll('.grid--view-items .grid__item');
          var added = 0;

          for (var i = 0; i < newItems.length && added < needed; i++) {
            var card = newItems[i].querySelector('.product-card[data-product-price-min]');
            if (card && self._isOutOfRange(card, filterMin, filterMax)) continue;
            gridContainer.appendChild(newItems[i].cloneNode(true));
            added++;
          }

          // If we still need more, try the next page
          var remaining = needed - added;
          if (remaining > 0) {
            var nextUrl = self._getNextPageUrl(doc);
            if (nextUrl) {
              self._fetchBackfillPage(nextUrl, filterMin, filterMax, remaining);
            }
          }
        })
        .catch(function() { /* silently fail */ });
    };

    /* --- Global search across all filter groups --- */
    CollectionFilters.prototype._bindFilterSearch = function() {
      document.addEventListener('input', function(e) {
        var input = e.target;
        if (!input.matches || !input.matches('[data-filter-search-global]')) return;

        var query = input.value.toLowerCase().trim();
        var sidebar = input.closest('.collection-filters');
        if (!sidebar) return;

        var groups = sidebar.querySelectorAll('[data-filter-group]');

        groups.forEach(function(group) {
          var items = group.querySelectorAll('.collection-filters__list-item, .collection-filters__list > li, .collection-filters__color-list > li');
          var hasVisible = false;

          if (!query) {
            // No search — show everything, restore group visibility
            items.forEach(function(item) { item.style.display = ''; });
            group.style.display = '';
            // Collapse groups back to their aria state
            var content = group.querySelector('.collection-filters__group-content');
            var header = group.querySelector('.collection-filters__group-header');
            if (content && header) {
              content.classList.remove('is-collapsed');
              if (header.getAttribute('aria-expanded') === 'false') {
                content.classList.add('is-collapsed');
              }
            }
            return;
          }

          items.forEach(function(item) {
            var text = item.textContent.toLowerCase();
            if (text.indexOf(query) !== -1) {
              item.style.display = '';
              hasVisible = true;
            } else {
              item.style.display = 'none';
            }
          });

          // If no items match in this group and it has list items, hide the group
          // But always show groups without list items (like Price)
          if (items.length === 0) {
            group.style.display = '';
          } else if (hasVisible) {
            group.style.display = '';
            // Auto-expand the group when search matches (don't change aria-expanded so collapse restores on clear)
            var content = group.querySelector('.collection-filters__group-content');
            if (content) content.classList.remove('is-collapsed');
          } else {
            group.style.display = 'none';
          }
        });
      });
    };

    /* --- Preserve API filter query params on tag-based links --- */
    CollectionFilters.prototype._syncTagLinksWithQueryParams = function() {
      var queryString = window.location.search;
      if (!queryString) return;

      // Get all tag-based filter links (generated by link_to_add_tag / link_to_remove_tag)
      var tagLinks = document.querySelectorAll('.collection-filters__tag-link, [data-action="remove-tag-filter"]');
      tagLinks.forEach(function(link) {
        var href = link.getAttribute('href');
        if (!href) return;

        // Strip any existing query string from the tag link
        var basePath = href.split('?')[0];

        // Append the current page's query params (API filters, sort, etc.)
        link.setAttribute('href', basePath + queryString);
      });

      // Also fix "Clear all" link to preserve API filters
      // (already handled by data-action="clear-filters" which uses href directly)
    };

    /* --- Sort dropdown --- */
    CollectionFilters.prototype._bindSortDropdown = function() {
      var dropdown = document.querySelector('[data-sort-dropdown]');
      if (!dropdown) return;

      var trigger = dropdown.querySelector('.collection-sort-dropdown__trigger');
      var list = dropdown.querySelector('.collection-sort-dropdown__list');
      if (!trigger || !list) return;

      trigger.addEventListener('click', function(e) {
        e.stopPropagation();
        var expanded = trigger.getAttribute('aria-expanded') === 'true';
        trigger.setAttribute('aria-expanded', String(!expanded));
        list.classList.toggle('is-open');
      });

      // Option selection
      list.addEventListener('click', function(e) {
        var option = e.target.closest('[data-sort-value]');
        if (!option) return;

        var value = option.getAttribute('data-sort-value');
        trigger.setAttribute('aria-expanded', 'false');
        list.classList.remove('is-open');

        // Update display
        var currentLabel = trigger.querySelector('[data-sort-current]');
        if (currentLabel) currentLabel.textContent = option.textContent;

        // Update selected state
        list.querySelectorAll('[data-sort-value]').forEach(function(li) {
          li.classList.remove('is-selected');
        });
        option.classList.add('is-selected');

        // Update URL and reload
        var url = new URL(window.location.href);
        url.searchParams.set('sort_by', value);
        url.searchParams.delete('page');
        this._navigateAndReload(url.toString());
      }.bind(this));

      // Close on outside click
      document.addEventListener('click', function(e) {
        if (!e.target.closest('[data-sort-dropdown]')) {
          trigger.setAttribute('aria-expanded', 'false');
          list.classList.remove('is-open');
        }
      });
    };

    /* --- Mobile filter drawer --- */
    CollectionFilters.prototype._bindMobileDrawer = function() {
      document.addEventListener('click', function(e) {
        if (e.target.closest('[data-action="open-mobile-filters"]')) {
          e.preventDefault();
          var drawer = document.getElementById('MobileFiltersDrawer');
          if (drawer) {
            drawer.setAttribute('aria-hidden', 'false');
            document.body.classList.add('mobile-filters-open');
          }
        }

        if (e.target.closest('[data-action="close-mobile-filters"]')) {
          e.preventDefault();
          var drawer2 = document.getElementById('MobileFiltersDrawer');
          if (drawer2) {
            drawer2.setAttribute('aria-hidden', 'true');
            document.body.classList.remove('mobile-filters-open');
          }
        }
      });

      // Close on Escape
      document.addEventListener('keyup', function(e) {
        if (e.key === 'Escape') {
          var drawer = document.getElementById('MobileFiltersDrawer');
          if (drawer && drawer.getAttribute('aria-hidden') === 'false') {
            drawer.setAttribute('aria-hidden', 'true');
            document.body.classList.remove('mobile-filters-open');
          }
        }
      });
    };

    /* --- Filter group collapse/expand --- */
    CollectionFilters.prototype._bindFilterToggles = function() {
      document.querySelectorAll('[data-action="toggle-filter"]').forEach(function(btn) {
        // Remove old listeners by cloning
        var newBtn = btn.cloneNode(true);
        btn.parentNode.replaceChild(newBtn, btn);

        newBtn.addEventListener('click', function() {
          var expanded = this.getAttribute('aria-expanded') === 'true';
          this.setAttribute('aria-expanded', String(!expanded));
          var group = this.closest('[data-filter-group]');
          var content = group ? group.querySelector('.collection-filters__group-content') : null;
          if (content) {
            content.classList.toggle('is-collapsed');
          }
          // Re-check sidebar scroll fade after content height changes
          var sidebar = this.closest('.collection-layout__sidebar');
          if (sidebar) {
            setTimeout(function() {
              var notScrollable = sidebar.scrollHeight <= sidebar.clientHeight;
              var atBottom = sidebar.scrollHeight - sidebar.scrollTop - sidebar.clientHeight < 5;
              sidebar.classList.toggle('is-scrolled-bottom', notScrollable || atBottom);
            }, 50);
          }
        });
      });
    };

    return CollectionFilters;
  })();

  /* -----------------------------------------------------------------------
     7. CART PAGE QUANTITY BUTTONS
     ----------------------------------------------------------------------- */

  function initCartPageQty() {
    var pending = false;

    document.addEventListener('click', function(e) {
      var btn = e.target.closest('[data-action="cart-decrease"], [data-action="cart-increase"]');
      if (!btn) return;
      if (pending) return;

      var itemKey = btn.getAttribute('data-item-key');
      var row = btn.closest('.cart-v2-item');
      var input = btn.closest('.cart-v2-qty-stepper').querySelector('.cart-v2-qty-input');
      if (!input || !itemKey) return;

      var current = parseInt(input.value, 10) || 1;
      var newQty;
      if (btn.getAttribute('data-action') === 'cart-decrease') {
        newQty = Math.max(0, current - 1);
      } else {
        newQty = current + 1;
      }

      if (newQty === current) return;

      // Optimistically sync all qty inputs for this row (desktop + mobile)
      var rowInputs = row ? row.querySelectorAll('.cart-v2-qty-input') : [input];
      rowInputs.forEach(function(el) { el.value = newQty; });
      pending = true;

      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: itemKey, quantity: newQty })
      })
      .then(function(r) {
        return r.json().then(function(data) {
          return { ok: r.ok, data: data };
        });
      })
      .then(function(result) {
        function showCartPageError(message) {
          if (!row) return;
          var existing = row.parentNode.querySelector('.cart-v2-item-error[data-error-key="' + itemKey + '"]');
          if (existing) existing.remove();
          var msg = document.createElement('div');
          msg.className = 'cart-v2-item-error';
          msg.setAttribute('data-error-key', itemKey);
          msg.textContent = message;
          row.parentNode.insertBefore(msg, row.nextSibling);
          setTimeout(function() { msg.remove(); }, 4000);
        }

        if (!result.ok) {
          // 422 — revert inputs and show error
          rowInputs.forEach(function(el) { el.value = current; });
          var errorMsg = (result.data && result.data.description) || 'Maximum quantity reached for this item';
          showCartPageError(errorMsg);
          pending = false;
          return;
        }

        var cart = result.data;
        if (cart && cart.items) {
          var actualItem = null;
          for (var i = 0; i < cart.items.length; i++) {
            if (cart.items[i].key === itemKey) {
              actualItem = cart.items[i];
              break;
            }
          }

          if (actualItem) {
            rowInputs.forEach(function(el) { el.value = actualItem.quantity; });
            if (actualItem.quantity < newQty) {
              showCartPageError('Only ' + actualItem.quantity + ' available for this item');
            }
          }

          // If quantity became 0, reload to remove the row
          if (newQty === 0 || (!actualItem && newQty === 0)) {
            window.location.reload();
            return;
          }

          // Update cart counts and totals
          document.querySelectorAll('#CartCount span:first-child, .cart-drawer__count').forEach(function(el) {
            el.textContent = cart.item_count;
          });
          var totalEl = document.querySelector('.cart-v2-sidebar-total .cart-v2-sidebar-value');
          if (totalEl) totalEl.textContent = formatMoney(cart.total_price);

          if (actualItem && row) {
            var linePrice = formatMoney(actualItem.final_line_price || actualItem.line_price);
            row.querySelectorAll('.cart-v2-item-total strong, .cart-v2-mobile-total strong').forEach(function(el) {
              el.textContent = linePrice;
            });
          }
        }
        pending = false;
      })
      .catch(function() {
        rowInputs.forEach(function(el) { el.value = current; });
        pending = false;
      });
    });
  }

  /* -----------------------------------------------------------------------
     SELL SEARCH
     ----------------------------------------------------------------------- */

  theme.SellSearch = (function() {
    function SellSearch(input) {
      this.input = input;
      this.grid = document.getElementById('sell-categories-grid');
      this.resultsWrap = document.getElementById('sell-search-results');
      if (!this.resultsWrap) return;

      this.resultsGrid = this.resultsWrap.querySelector('.sell-search-results__grid');
      this.resultsCount = this.resultsWrap.querySelector('.sell-search-results__count');
      this.resultsEmpty = this.resultsWrap.querySelector('.sell-search-results__empty');
      this.spinner = input.parentElement.querySelector('.sell-search__spinner');
      this.clearBtn = input.parentElement.querySelector('.sell-search__clear');
      this.currentRequest = null;

      this._bindEvents();
    }

    SellSearch.prototype._bindEvents = function() {
      this.input.addEventListener('input', debounce(this._onInput.bind(this), 250));

      if (this.clearBtn) {
        this.clearBtn.addEventListener('click', function() {
          this.input.value = '';
          this._showGrid();
          this.clearBtn.style.display = 'none';
          this.input.focus();
        }.bind(this));
      }
    };

    SellSearch.prototype._onInput = function() {
      var query = this.input.value.trim();

      if (this.clearBtn) {
        this.clearBtn.style.display = query.length > 0 ? '' : 'none';
      }

      if (query.length < 2) {
        this._showGrid();
        return;
      }

      this._doSearch(query);
    };

    SellSearch.prototype._doSearch = function(query) {
      if (this.spinner) this.spinner.classList.add('is-active');

      if (this.currentRequest) {
        this.currentRequest.abort();
      }

      var controller = new AbortController();
      this.currentRequest = controller;

      var url = '/search/suggest.json?q=' + encodeURIComponent(query) +
                '&resources[type]=product' +
                '&resources[limit]=10';

      fetch(url, { signal: controller.signal })
        .then(function(r) { return r.json(); })
        .then(function(data) {
          if (this.spinner) this.spinner.classList.remove('is-active');
          this.currentRequest = null;

          var products = data.resources && data.resources.results && data.resources.results.products
            ? data.resources.results.products
            : [];

          this._renderResults(products, query);
        }.bind(this))
        .catch(function(err) {
          if (err.name !== 'AbortError') {
            if (this.spinner) this.spinner.classList.remove('is-active');
            this.currentRequest = null;
          }
        }.bind(this));
    };

    SellSearch.prototype._renderResults = function(products, query) {
      while (this.resultsGrid.firstChild) {
        this.resultsGrid.removeChild(this.resultsGrid.firstChild);
      }

      if (this.grid) this.grid.style.display = 'none';
      this.resultsWrap.style.display = '';

      if (products.length === 0) {
        this.resultsCount.textContent = '';
        this.resultsEmpty.style.display = '';
        return;
      }

      this.resultsEmpty.style.display = 'none';
      this.resultsCount.textContent = products.length + ' result' + (products.length !== 1 ? 's' : '') + ' found';

      for (var i = 0; i < products.length; i++) {
        var p = products[i];
        var item = document.createElement('a');
        item.href = p.url;
        item.className = 'sell-search-results__item col-12 col-md-6';

        // Image
        if (p.image) {
          var imgWrap = document.createElement('div');
          imgWrap.className = 'sell-search-results__item-image';
          var img = document.createElement('img');
          img.src = p.image.replace(/(\.[^.]+)$/, '_200x$1');
          img.alt = '';
          img.loading = 'lazy';
          imgWrap.appendChild(img);
          item.appendChild(imgWrap);
        }

        // Info
        var info = document.createElement('div');
        info.className = 'sell-search-results__item-info';
        var title = document.createElement('p');
        title.className = 'sell-search-results__item-title';
        this._highlightInto(title, p.title || '', query);
        info.appendChild(title);
        item.appendChild(info);

        // Price
        if (p.price_min) {
          var price = document.createElement('span');
          price.className = 'sell-search-results__item-price';
          price.textContent = formatMoney(parseFloat(p.price_min) * 100);
          item.appendChild(price);
        }

        this.resultsGrid.appendChild(item);
      }
    };

    /**
     * Splits text on query matches and appends text nodes / <mark> elements
     * into the given parent — no innerHTML, no XSS surface.
     */
    SellSearch.prototype._highlightInto = function(parent, text, query) {
      if (!query) {
        parent.textContent = text;
        return;
      }
      var escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      var regex = new RegExp('(' + escaped + ')', 'gi');
      var parts = text.split(regex);
      for (var i = 0; i < parts.length; i++) {
        if (regex.test(parts[i])) {
          var mark = document.createElement('mark');
          mark.textContent = parts[i];
          parent.appendChild(mark);
        } else {
          parent.appendChild(document.createTextNode(parts[i]));
        }
        // Reset lastIndex because we reuse the regex with global flag
        regex.lastIndex = 0;
      }
    };

    SellSearch.prototype._showGrid = function() {
      if (this.grid) this.grid.style.display = '';
      if (this.resultsWrap) this.resultsWrap.style.display = 'none';
      if (this.spinner) this.spinner.classList.remove('is-active');
    };

    return SellSearch;
  })();

  /* -----------------------------------------------------------------------
     INITIALIZATION
     ----------------------------------------------------------------------- */

  document.addEventListener('DOMContentLoaded', function() {
    // Announcement bar
    var announcementEl = document.querySelector('[data-section-type="announcement-bar"]');
    if (announcementEl) {
      new theme.AnnouncementBar(announcementEl);
    }

    // Cart drawer
    theme.cartDrawerInstance = new theme.CartDrawer();

    // Predictive search
    new theme.PredictiveSearch();

    // Mega menu
    new theme.MegaMenu();

    // Mobile menu drawer
    new theme.MobileMenuDrawer();

    // Cart page quantity buttons
    initCartPageQty();

    // Sidebar scroll fade hint
    document.querySelectorAll('.collection-layout__sidebar').forEach(function(sidebar) {
      function checkScroll() {
        var notScrollable = sidebar.scrollHeight <= sidebar.clientHeight;
        var atBottom = sidebar.scrollHeight - sidebar.scrollTop - sidebar.clientHeight < 5;
        sidebar.classList.toggle('is-scrolled-bottom', notScrollable || atBottom);
      }
      sidebar.addEventListener('scroll', checkScroll);
      checkScroll();
    });

    // Collection filters
    var collectionEl = document.querySelector('[data-section-type="collection-template"]');
    if (collectionEl) {
      new theme.CollectionFilters(collectionEl);
    }

    // Sell search
    var sellSearchInput = document.getElementById('sell-search-input');
    if (sellSearchInput) {
      new theme.SellSearch(sellSearchInput);
    }

    // Reviews carousel (Slick)
    var quotesSlider = document.querySelector('[data-section-type="quotes"] .quotes-slider');
    if (quotesSlider && typeof $ !== 'undefined') {
      $(quotesSlider).slick({
        slidesToShow: 1,
        slidesToScroll: 1,
        draggable: true,
        infinite: true,
        arrows: true,
        prevArrow: '.reviews-arrow--prev',
        nextArrow: '.reviews-arrow--next',
        adaptiveHeight: true,
        dots: false
      });
    }
  });

})();
